// Memory Web icons (docs/07 §Memory Web): 12×12 gate glyphs drawn inside the gold `node_gate`
// medallion, 8×8 branch glyphs for the silver `node_stat` nodes. Both sit on a dark window, so
// the fills are bright and the backgrounds transparent. 1-px ink outline, light from top-left.
// Key: '.' transparent, 'o' ink, every other char from the shared map below.
import type { PaletteKey } from '../palette';
import { sprite, type SpriteDef } from './types';

const K: Record<string, PaletteKey> = {
  o: 'ink',
  w: 'white',
  p: 'paper',
  s: 'steel',
  g: 'lilacGrey',
  d: 'dusk',
  l: 'lemon',
  y: 'gold',
  b: 'brass',
  B: 'bronze',
  C: 'copper',
  K: 'brick',
  m: 'maroon',
  a: 'amber',
  r: 'red',
  R: 'blood',
  k: 'coral',
  f: 'flesh',
  F: 'crimson',
  P: 'salmonPink',
  c: 'cream',
  A: 'apricot',
  T: 'sand',
  e: 'leaf',
  E: 'clover',
  G: 'forest',
  M: 'moss',
  O: 'olive',
  D: 'mud',
  t: 'mint',
  u: 'sky',
  U: 'blue',
  x: 'lavender',
};

/** Paints `layers` (rows, x, y) onto a transparent w×h canvas; later layers draw on top. */
function compose(w: number, h: number, layers: [string[], number, number][]): string[] {
  const grid = Array.from({ length: h }, () => Array<string>(w).fill('.'));
  for (const [rows, x0, y0] of layers)
    rows.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        const ch = row[x]!;
        if (ch !== '.' && y0 + y < h && x0 + x < w) grid[y0 + y]![x0 + x] = ch;
      }
    });
  return grid.map((r) => r.join(''));
}

// A small bony hand, fingers up (8×9), shared by the Hand gates.
const HAND = [
  '..o.o.o.',
  '.opopopo',
  '.osososo',
  '.opopopo',
  'oopopopo',
  'popopopo',
  'pppspsso',
  '.oppsso.',
  '..opso..',
];
const COIN = ['..oooo..', '.olyyBo.', 'olyBByBo', 'oyByyBBo', 'oyByyBBo', 'oyyBBBCo', '.oBBBCo.', '..oooo..'];
const PADLOCK = ['.oooo.', 'osssso', 'os..so', 'oooooo', 'olyyBo', 'oyyoBo', 'oBBoCo', 'oooooo'];
const DRUMSTICK = ['....ooo', '....owo', '..ooso.', '.oBBCo.', 'oBBCKo.', 'oCCKKo.', '.oooo..'];
const STAR = ['...o...', '..oyo..', 'oooyooo', 'olyyyBo', '.oyyBo.', 'oyBoBBo', 'oo...oo'];
const CHECK = ['....ooo', 'o...oeo', 'eo.oeo.', 'oeoeo..', '.oeo...', '..o....'];
const FORK = [
  '.o.o.o.',
  'owopoGo',
  'owoposo',
  'owppsgo',
  '.opsgo.',
  '..opo..',
  '..opo..',
  '..opo..',
  '.opsgo.',
  '.opsgo.',
  '.opsgo.',
  '..ooo..',
];

export const WEB_ICON_SPRITES: SpriteDef[] = [
  // ─── Gates (12×12) ───
  // The first node: a coin half-swallowed by the slot.
  sprite('gi_ROOT', K, [
    '....oooo....',
    '...olyyBo...',
    '..olyyyyBo..',
    '..oyyBByBo..',
    'oooyyyyyBooo',
    'owwoooooopso',
    'owpppppppsso',
    'opppppppssgo',
    'opsssssssggo',
    'osggggggggdo',
    '.oooooooooo.',
    '............',
  ]),
  // Nibble: two cherries on one stem.
  sprite('gi_G_PAIR', K, [
    '......ooo...',
    '.....eeEGo..',
    '....e.eoo...',
    '....e..e....',
    '...e....e...',
    '...e...oooo.',
    '.oooo.okkrro',
    'okkrrookrrRo',
    'okrrRoorrrRo',
    'orrrRoorRRRo',
    'orRRRo.oooo.',
    '.oooo.......',
  ]),
  // Hold: a padlock.
  sprite('gi_G_HOLD', K, [
    '...oooooo...',
    '..owssssgo..',
    '..oso..ogo..',
    '..oso..ogo..',
    '..oso..ogo..',
    '.oooooooooo.',
    '.olyyyyyyBo.',
    '.oyyyooyyBo.',
    '.oyyyooyyBo.',
    '.oyyyyoyBBo.',
    '.oBBBBBBBCo.',
    '.oooooooooo.',
  ]),
  // Taste: a fork.
  sprite('gi_G_DRAFT', K, [
    '....o.o.o...',
    '...owoposo..',
    '...owoposo..',
    '...owppsgo..',
    '....opsgo...',
    '.....opo....',
    '.....opo....',
    '.....opo....',
    '....opsgo...',
    '....opsgo...',
    '....opsgo...',
    '.....ooo....',
  ]),
  // Nudge: one arrow up, one down.
  sprite('gi_G_NUDGE', K, [
    '..oo........',
    '.owso.......',
    'owppso......',
    'oowsoo.oooo.',
    '.owso..owso.',
    '.owso..owso.',
    '.owso..owso.',
    '.owso..owso.',
    '.oooo.oowsoo',
    '......owppso',
    '.......owso.',
    '........oo..',
  ]),
  // Trinkets: a gem charm on a brass chain.
  sprite('gi_G_CHARMS', K, [
    'b..........b',
    '.b........b.',
    '..b......b..',
    '...b....b...',
    '....bbbb....',
    '.....oo.....',
    '....owto....',
    '...owttEo...',
    '..owtttEGo..',
    '...otEEGo...',
    '....oEGo....',
    '.....oo.....',
  ]),
  // Scratch of Fate: a half-scratched card and a coin.
  sprite('gi_G_BONUS_SCRATCH', K, [
    'ooooooooo...',
    'owppppppo...',
    'opgggggpo...',
    'opgyyggpo...',
    'opggyygpo...',
    'opgggggpo...',
    'oppppppoooo.',
    'oprrrpolyyBo',
    'opppppoyyyBo',
    'oooooooyyBBo',
    '......obBBCo',
    '.......oooo.',
  ]),
  // It Wakes: an eye opening under a heavy lid.
  sprite('gi_G_GROW_1', K, [
    '............',
    '..o.o..o.o..',
    '...oooooo...',
    '.ooPPfffFoo.',
    'oPPfffffFFFo',
    'oooooooooooo',
    'owwoyooyowso',
    '.owoyooyoso.',
    '..owooooso..',
    '...oooooo...',
    '............',
    '............',
  ]),
  // Pillars: three lit columns under a lintel.
  sprite('gi_G_COLUMNS', K, [
    '............',
    'oooooooooooo',
    'ollllyyyyyBo',
    'oooooooooooo',
    '.oyBoyBoyBo.',
    '.oyBoyBoyBo.',
    '.oyBoyBoyBo.',
    '.oyBoyBoyBo.',
    '.oyBoyBoyBo.',
    'oooooooooooo',
    'obbbbbBBBBCo',
    'oooooooooooo',
  ]),
  // Crooked Lines: a lit diagonal across a dim 3×3 grid.
  sprite('gi_G_DIAGONALS', K, [
    '........oooo',
    '.dd..dd.olyo',
    '.dd..dd.oyBo',
    '........oooo',
    '....oooo....',
    '.dd.olyo.dd.',
    '.dd.oyBo.dd.',
    '....oooo....',
    'oooo........',
    'olyo.dd..dd.',
    'oyBo.dd..dd.',
    'oooo........',
  ]),
  // Feeding: a morsel dropping into a toothy mouth.
  sprite('gi_G_FEED', K, [
    '....oooo....',
    '...oAAkro...',
    '...okrrRo...',
    '....oooo....',
    '..oooooooo..',
    '.oPffffffFo.',
    'ofwowowowoFo',
    'ofmmmmmmmmFo',
    'ofmmmmmmmmFo',
    'ofowowowowFo',
    '.oFFFFFFFFo.',
    '..oooooooo..',
  ]),
  // Spoilage: a spotted, rotting apple and its fly.
  sprite('gi_G_ROT', K, [
    '........p.p.',
    '.........o..',
    '....oDo.....',
    '..oooDooo...',
    '.oMMMoOOOo..',
    'oMMOOOOOOo..',
    'oMODDOOOOo..',
    'oOODDOODOo..',
    'oOOOOODDOo..',
    '.oOOOODDo...',
    '..oDDDDo....',
    '...oooo.....',
  ]),
  // Propositions: a contract scroll with a red wax seal.
  sprite('gi_G_PROPOSITIONS', K, [
    '.oooooooooo.',
    'occccccccTTo',
    '.oooooooooo.',
    '.occcccccTo.',
    '.ocddddccTo.',
    '.occcccccTo.',
    '.ocdddddcTo.',
    '.occccccooo.',
    '.occcccokrro',
    'occcccorrrRo',
    '.oooooorRRo.',
    '.......ooo..',
  ]),
  // Double or Devoured: a heart card over a face-down card.
  sprite('gi_G_BONUS_HILO', K, [
    '....oooooooo',
    '....oFrFrFro',
    'ooooooooFrFo',
    'orwwwwporFro',
    'owwwwwpoFrFo',
    'owrwrwporFro',
    'orrrrrpoFrFo',
    'owrrrwporFro',
    'owwrwwpoFrFo',
    'owwwwwpooooo',
    'opppprso....',
    'oooooooo....',
  ]),
  // It Stretches: a double arrow, left and right.
  sprite('gi_G_GROW_2', K, [
    '............',
    '............',
    '...oo..oo...',
    '..oyo..oyo..',
    '.oyyooooyyo.',
    'oyyyyyyyyyyo',
    'oBBBBBBBBBBo',
    '.oBBooooBBo.',
    '..oBo..oBo..',
    '...oo..oo...',
    '............',
    '............',
  ]),
  // Fine Print: a red rubber stamp.
  sprite('gi_G_HEX', K, [
    '....oooo....',
    '...okrrRo...',
    '...orrRRo...',
    '....oooo....',
    '....oCKo....',
    '....oCKo....',
    '..oooooooo..',
    '.oTTTTTTDDo.',
    '.orrrrrrRRo.',
    '.oooooooooo.',
    '............',
    '..rrr..rrr..',
  ]),
  // Wheel of Fate: a four-colour wheel under its pointer.
  sprite('gi_G_BONUS_WHEEL', K, [
    '...oooooo...',
    '....owpo....',
    '....oooo....',
    '..oorryyoo..',
    '..orrryyyo..',
    '.orrrryyyyo.',
    '.orrrooyyyo.',
    '.otttooxxxo.',
    '.ottttxxxxo.',
    '..otttxxxo..',
    '..oottxxoo..',
    '....oooo....',
  ]),
  // It Widens: a double arrow pushing two brackets apart.
  sprite('gi_G_GROW_3', K, [
    '............',
    'pp........pp',
    'p..........p',
    'p..oo..oo..p',
    'p.oyooooyo.p',
    'poyyyyyyyyop',
    'poBBBBBBBBop',
    'p.oBooooBo.p',
    'p..oo..oo..p',
    'p..........p',
    'pp........pp',
    '............',
  ]),
  // The Grin: a V.
  sprite('gi_G_VEE', K, [
    '............',
    'oooo....oooo',
    'olyo....oyBo',
    'oyyo....oyBo',
    '.oyyo..oyBo.',
    '.oyyo..oyBo.',
    '..oyyooyBo..',
    '..oyyooyBo..',
    '...oyyyBo...',
    '....oyBo....',
    '.....oo.....',
    '............',
  ]),
  // Stutter: a zigzag.
  sprite('gi_G_ZIG', K, [
    '............',
    '............',
    '............',
    'o.....o.....',
    'yo...oyo...o',
    'Byo.oyByo.oy',
    'oByoyBoByoyB',
    '.oByBo.oByBo',
    '..oBo...oBo.',
    '...o.....o..',
    '............',
    '............',
  ]),
  // Molt: a cracked shell.
  sprite('gi_G_MOLT', K, [
    '....oooo....',
    '...owwoso...',
    '..owwopsso..',
    '..owppopso..',
    '.owpppposso.',
    '.owpppopsso.',
    '.oppppposgo.',
    '.opppppssgo.',
    '.ospppssggo.',
    '..ossssggo..',
    '...oggggo...',
    '....oooo....',
  ]),
  // It Sees You: a wide, staring eye.
  sprite('gi_G_EYES', K, [
    '............',
    '...o.oo.o...',
    '....oooo....',
    '..oowwwwoo..',
    '.owwwrrwwso.',
    'owwwrooRwwso',
    'owwprooRpsso',
    '.opppRRpsso.',
    '..oossggoo..',
    '....oooo....',
    '....o..o....',
    '............',
  ]),
  // The Eye: a ring of lit cells around a dark centre.
  sprite('gi_G_EYE_PATTERN', K, [
    'oooooooooooo',
    'olyoolyoolyo',
    'oyBooyBooyBo',
    'oooooooooooo',
    'oooo....oooo',
    'olyo.dd.olyo',
    'oyBo.dd.oyBo',
    'oooo....oooo',
    'oooooooooooo',
    'olyoolyoolyo',
    'oyBooyBooyBo',
    'oooooooooooo',
  ]),
  // Crucifix: a gold cross.
  sprite('gi_G_CROSS', K, [
    '....oooo....',
    '....olyo....',
    '.ooooyBoooo.',
    '.olyyyyyyyo.',
    '.oBBBBBBBBo.',
    '.ooooyBoooo.',
    '....oyBo....',
    '....oyBo....',
    '....oyBo....',
    '....oyBo....',
    '....oyBo....',
    '....oooo....',
  ]),
  // Teeth Drop: a ball falling through rows of teeth.
  sprite('gi_G_BONUS_PLINKO', K, [
    '.....oo.....',
    '....olyo....',
    '....oyBo....',
    '..o..oo..o..',
    '.owo....owo.',
    '.owo....owo.',
    'owwso..owwso',
    'owpsoo.owpso',
    '....owo.....',
    '....owo.....',
    '...owwso....',
    '...owpso....',
  ]),
  // It Deepens: a double arrow, up and down.
  sprite('gi_G_GROW_4', K, [
    '.....oo.....',
    '....oyBo....',
    '...oyyBBo...',
    '..oyyyBBBo..',
    '..oooyBooo..',
    '....oyBo....',
    '....oyBo....',
    '..oooyBooo..',
    '..oyyyBBBo..',
    '...oyyBBo...',
    '....oyBo....',
    '.....oo.....',
  ]),
  // The Feast: a roast on a plate.
  sprite('gi_G_BONUS_FEAST', K, [
    '.........oo.',
    '........owwo',
    '.......owso.',
    '...oooooso..',
    '..obBBCCKo..',
    '.obBBCCCKKo.',
    '.oBBCCCKKKo.',
    '.oCCKKKKmmo.',
    'oooooooooooo',
    'owwwppppppso',
    '.ossssssggo.',
    '..oooooooo..',
  ]),
  // Cathedral: a pointed window, rose above, two lancets below.
  sprite('gi_G_GROW_5', K, [
    '.....oo.....',
    '....oppo....',
    '...opyyso...',
    '..opyllyso..',
    '..opyyyyso..',
    '.opooooooso.',
    '.opuxoorFso.',
    '.opxuooFrso.',
    '.opuxoorFso.',
    '.opxuooFrso.',
    'oppppppsssso',
    'oooooooooooo',
  ]),
  // The Hand: a bony hand, fingers raised.
  sprite('gi_H_AUTOSPIN', K, [
    '.....o.o....',
    '...oopopo...',
    '..opopopoo..',
    '..osososopo.',
    '..opopopopo.',
    '..opopoposo.',
    '.oopopopopo.',
    'opopopopopo.',
    '.oppspspsso.',
    '..opppssso..',
    '....oposo...',
    '....oposo...',
  ]),
  // Quicker Hand: the hand with speed lines trailing it.
  sprite(
    'gi_H_SPEED',
    K,
    compose(12, 12, [
      [['uuu.', '....', '.uuu', '....', 'uuu.'], 0, 5],
      [HAND, 4, 3],
    ]),
  ),
  // Punctual Hand: a coin, checked off.
  sprite(
    'gi_H_AUTOPAY',
    K,
    compose(12, 12, [
      [COIN, 0, 4],
      [CHECK, 5, 1],
    ]),
  ),
  // Clinging Hand: the hand clutching a padlock.
  sprite(
    'gi_H_AUTOHOLD',
    K,
    compose(12, 12, [
      [HAND, 0, 3],
      [PADLOCK, 6, 4],
    ]),
  ),
  // Feeding Hand: the hand offering a drumstick.
  sprite(
    'gi_H_AUTOFEED',
    K,
    compose(12, 12, [
      [HAND, 0, 3],
      [DRUMSTICK, 5, 0],
    ]),
  ),
  // Nimble Hand: one raised finger between an up and a down arrow.
  sprite('gi_H_AUTONUDGE', K, [
    '..oo........',
    '.opso....o..',
    '.opso...owo.',
    '.osso..owpso',
    '.opso..ooooo',
    '.opsooo.....',
    'oopsppo.....',
    'opppssoooooo',
    'opppssoowpso',
    '.opsso..oso.',
    '..oso....o..',
    '..ooo.......',
  ]),
  // Tasting Hand: the hand gripping a fork.
  sprite(
    'gi_H_AUTODRAFT',
    K,
    compose(12, 12, [
      [FORK, 5, 0],
      [HAND, 0, 3],
    ]),
  ),
  // Playful Hand: the hand reaching for a star.
  sprite(
    'gi_H_AUTOBONUS',
    K,
    compose(12, 12, [
      [HAND, 0, 3],
      [STAR, 5, 0],
    ]),
  ),

  // ─── Branches (8×8) ───
  sprite('bi_fortune', K, [
    '.oo.oo..',
    'oeEoeEo.',
    'oEGEEGo.',
    '.oGGGo..',
    'oeEGeEo.',
    'oEGoEGo.',
    '.oo.ooe.',
    '.......e',
  ]),
  sprite('bi_greed', K, COIN),
  sprite('bi_hands', K, HAND.slice(0, 8)),
  sprite('bi_contracts', K, [
    '.....ooo',
    '....owwo',
    '...owwpo',
    '..owwpso',
    '..owpso.',
    '.oopso..',
    '.oyoo...',
    '.y......',
  ]),
  sprite('bi_appetite', K, [
    'oooooooo',
    'offfffFo',
    'owwwwpso',
    '.owwpso.',
    '.owpso..',
    '..owso..',
    '..oso...',
    '...o....',
  ]),
  sprite('bi_rituals', K, [
    '...oo...',
    '..olyo..',
    '..oyao..',
    '.oooooo.',
    '.owccAo.',
    '.occcAo.',
    '.occAAo.',
    '.oooooo.',
  ]),
];
